import React from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  Building2, 
  Users, 
  FileText, 
  Scale, 
  Landmark,
  Target,
  BarChart3,
  Briefcase,
  Calculator,
  Award,
  ShieldCheck,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema } from '../utils/seoSchemas';
import RelatedServices from '../components/RelatedServices';
const ValuationServices = () => {
  usePageSeo({
    title: 'Business Valuation Services in UAE',
    description: 'Professional UAE business valuation services — for M&A, financial reporting, share transfers, disputes and strategic decisions.',
    path: '/services/valuation'
  });

  const services = [
    {
      icon: Building2,
      title: 'Business Valuation',
      description: 'We assist companies, investors, and shareholders in determining the fair value of businesses across various industries in the UAE.',
      applications: [
        'Mergers & acquisitions',
        'Shareholder restructuring and buyouts',
        'Corporate Tax restructuring support',
        'Investment entry and exit planning',
        'Partner admission or ESOP valuation'
      ],
      methodologies: [
        'Discounted Cash Flow (DCF)',
        'Market Comparable Analysis',
        'Asset-Based Approach'
      ]
    },
    {
      icon: TrendingUp,
      title: 'Startup & SME Valuation',
      description: 'Designed for growing businesses seeking investor readiness or strategic clarity.',
      services: [
        'Pre-money and post-money valuation',
        'Fundraising support',
        'Financial modelling and valuation reports',
        'Investor presentation support'
      ]
    },
    {
      icon: FileText,
      title: 'Financial Reporting & Fair Value Valuation (IFRS Support)',
      description: 'We provide valuation assistance aligned with international accounting standards to support auditors, management, and finance teams.',
      coverage: [
        'IFRS 13 Fair Value Measurement',
        'Investment and financial instrument valuation',
        'Purchase Price Allocation (PPA)',
        'Goodwill impairment analysis'
      ]
    },
    {
      icon: Award,
      title: 'Intangible Asset & Brand Valuation',
      description: 'Our specialists help businesses quantify the value of non-physical assets that drive long-term growth.',
      examples: [
        'Trademarks and brand valuation',
        'Customer relationship valuation',
        'Software and intellectual property valuation',
        'Licensing and royalty analysis'
      ]
    },
    {
      icon: Scale,
      title: 'Litigation & Dispute Valuation Support',
      description: 'We assist legal advisors and business owners with independent financial analysis during disputes.',
      scope: [
        'Shareholder and partnership disputes',
        'Economic loss assessments',
        'Expert financial analysis',
        'Settlement support documentation'
      ]
    },
    {
      icon: Landmark,
      title: 'Fixed Asset & Investment Valuation',
      description: 'We provide valuation support for operational and investment assets used in financing, restructuring, or reporting.',
      services: [
        'Machinery and equipment valuation (financial reporting support)',
        'Investment portfolio valuation',
        'Asset-based valuation reviews'
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: ShieldCheck,
      title: 'Independent & Objective',
      description: 'Unbiased valuations backed by rigorous analysis'
    },
    {
      icon: Target,
      title: 'Commercially Practical',
      description: 'Real-world insights that support decision-making'
    },
    {
      icon: BarChart3,
      title: 'International Standards',
      description: 'IFRS-aligned methodologies and best practices'
    },
    {
      icon: Briefcase,
      title: 'Industry Expertise',
      description: 'Deep understanding of UAE market dynamics'
    }
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-val-service"
        data={serviceSchema({
          name: 'Business Valuation Services in UAE',
          description:
            'Professional business valuation services for M&A, financial reporting, share transfers, disputes and strategic decisions in the UAE.',
          path: '/services/valuation',
          serviceType: 'Business Valuation Services'
        })}
      />
      <JsonLd
        id="ld-val-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Valuation Services', path: '/services/valuation' }
        ])}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-900 via-teal-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Calculator className="text-teal-400" size={32} />
              <span className="text-teal-400 font-semibold text-sm uppercase tracking-wider">Valuation Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Valuation Services
            </h1>
            <p className="text-xl text-gray-300 mb-4 font-semibold">
              White Paper Accounts Auditing – UAE
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-3xl">
              At White Paper Accounts Auditing, we provide independent and commercially practical valuation services to support financial reporting, transactions, regulatory compliance, and strategic decision-making. Our approach combines financial expertise, market insight, and internationally accepted valuation methodologies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=valuation">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg">
                  Get Valuation Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact?service=valuation">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Book Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg text-center">
                  <CardContent className="p-6">
                    <div className="bg-teal-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-teal-600" size={28} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Comprehensive Valuation Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From business valuations to intangible assets, we provide expert analysis for all your valuation needs.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              const listItems = service.applications || service.services || service.coverage || service.examples || service.scope || [];
              
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="bg-teal-100 w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="text-teal-600" size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                        <p className="text-gray-600">{service.description}</p>
                      </div>
                    </div>
                    
                    {listItems.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-700 mb-3">
                          {service.applications ? 'Key Applications:' : 
                           service.coverage ? 'Coverage:' : 
                           service.examples ? 'Examples:' : 
                           service.scope ? 'Scope:' : 'Services Include:'}
                        </p>
                        <ul className="space-y-2">
                          {listItems.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <CheckCircle className="text-teal-500 mt-0.5 flex-shrink-0" size={16} />
                              <span className="text-sm text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {service.methodologies && (
                      <div className="mt-4 bg-teal-50 rounded-lg p-4">
                        <p className="text-sm font-semibold text-teal-700 mb-3">Methodologies:</p>
                        <ul className="space-y-2">
                          {service.methodologies.map((method, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <CheckCircle className="text-teal-600 mt-0.5 flex-shrink-0" size={16} />
                              <span className="text-sm text-teal-800">{method}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

            <RelatedServices
        services={[
          { title: 'Business Advisory', description: 'Strategic advisory beyond valuation — growth, risk, restructuring.', path: '/business-advisory-uae', accent: 'emerald' },
          { title: 'Audit Services in UAE', description: 'Audited financials add credibility to every valuation report.', path: '/audit-services-uae', accent: 'green' },
          { title: 'Accounting Services', description: 'Clean records are the foundation of accurate business valuation.', path: '/accounting-services-uae', accent: 'indigo' }
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Need a Professional Valuation?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Whether for transactions, compliance, or strategic planning, our team is ready to provide independent, reliable valuation services.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact?service=valuation">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-6 text-lg">
                Request Valuation
              </Button>
            </Link>
            <Link to="/contact?service=valuation">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Schedule Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ValuationServices;
